import { usePage } from '@inertiajs/react';
import { CornerDownLeft, Search } from 'lucide-react';
import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
    filterSearchEntries,
    flattenMenuForSearch,
} from '../../lib/flattenMenu';
import { navigateToHref } from '../../lib/navigate';
import type { PageProps } from '../../types';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/Dialog';
import { cn } from '../ui/utils';

type GlobalSearchProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export default function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
    const page = usePage<PageProps>();
    const menu = page.props.menu ?? [];
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    const entries = useMemo(() => flattenMenuForSearch(menu), [menu]);
    const results = useMemo(() => {
        const filtered = filterSearchEntries(entries, query);
        if (!query.trim() && filtered.length > 48) {
            return filtered.slice(0, 48);
        }
        return filtered;
    }, [entries, query]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setQuery('');
        setActiveIndex(0);
        const t = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => window.clearTimeout(t);
    }, [open]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        if (activeIndex >= results.length) {
            setActiveIndex(results.length > 0 ? results.length - 1 : 0);
        }
    }, [activeIndex, results.length]);

    const selectEntry = useCallback(
        (index: number) => {
            const entry = results[index];
            if (!entry) {
                return;
            }
            onOpenChange(false);
            navigateToHref(entry.href, entry.target);
        },
        [onOpenChange, results],
    );

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const isMod = event.metaKey || event.ctrlKey;
            if (!isMod || event.key.toLowerCase() !== 'k') {
                return;
            }
            event.preventDefault();
            onOpenChange(!open);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onOpenChange, open]);

    const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!results.length) {
                return;
            }
            setActiveIndex((i) => (i + 1) % results.length);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!results.length) {
                return;
            }
            setActiveIndex((i) => (i - 1 + results.length) % results.length);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            selectEntry(activeIndex);
        }
    };

    const kbdLabel =
        typeof navigator !== 'undefined' &&
        /Mac|iPhone|iPad|iPod/i.test(navigator.platform)
            ? '⌘K'
            : 'Ctrl + K';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="nx-command-dialog">
                <DialogHeader className="sr-only">
                    <DialogTitle>Buscar no sistema</DialogTitle>
                    <DialogDescription>
                        Filtre itens do menu e navegue com o teclado.
                    </DialogDescription>
                </DialogHeader>

                <div className="nx-command-search">
                    <Search size={18} strokeWidth={2} aria-hidden />
                    <input
                        ref={inputRef}
                        type="search"
                        className="nx-command-input"
                        placeholder="Buscar páginas e menus…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleInputKeyDown}
                        aria-autocomplete="list"
                        aria-controls={listId}
                        aria-activedescendant={
                            results[activeIndex]
                                ? `${listId}-option-${activeIndex}`
                                : undefined
                        }
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <kbd>{kbdLabel}</kbd>
                </div>

                <div
                    id={listId}
                    className="nx-command-list"
                    role="listbox"
                    aria-label="Resultados da busca"
                >
                    {results.length === 0 ? (
                        <p className="nx-command-empty">
                            {query.trim()
                                ? `Nenhum item encontrado para “${query.trim()}”.`
                                : 'Nenhum item de menu disponível.'}
                        </p>
                    ) : (
                        results.map((entry, index) => (
                            <button
                                key={entry.id}
                                id={`${listId}-option-${index}`}
                                type="button"
                                role="option"
                                aria-selected={index === activeIndex}
                                className={cn(
                                    'nx-command-item',
                                    index === activeIndex &&
                                        'nx-command-item--active',
                                )}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => selectEntry(index)}
                            >
                                <span className="nx-command-item-text">
                                    <strong>{entry.label}</strong>
                                    {entry.groupLabel ? (
                                        <span>{entry.groupLabel}</span>
                                    ) : null}
                                </span>
                                <CornerDownLeft
                                    className="nx-command-item-hint"
                                    size={14}
                                    strokeWidth={2}
                                    aria-hidden
                                />
                            </button>
                        ))
                    )}
                </div>

                <p className="nx-command-footer">
                    Use ↑ ↓ para navegar e Enter para abrir. Esc fecha.
                </p>
            </DialogContent>
        </Dialog>
    );
}
